import React from "react";
import { usePathname, useRouter } from "next/navigation";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from "./ui/sidebar";
import { AnimatedRailItem } from "./animated-rail-item";
import { UpdateDialog } from "./update-dialog";
import { startNavigationFeedback } from "@/lib/navigation-feedback";

type NavPrimaryProps = {
  items: {
    title: string;
    url: string;
    icon?: any;
    className?: string;
    /** Renders an attention dot on the rail icon (e.g. pending join requests). */
    showDot?: boolean;
    visible?: boolean;
    animatePresence?: boolean;
  }[];
  showUpdateDialog?: boolean;
  updateContent?: React.ReactNode;
  onItemClick?: (item: { title: string; url: string }) => boolean | void;
} & React.ComponentProps<typeof SidebarGroup>;

export function NavPrimary({
  items,
  showUpdateDialog = false,
  updateContent,
  onItemClick,
  ...props
}: NavPrimaryProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { setOpenMobile, isMobile } = useSidebar();

  // Create a modified items array to render with the update dialog inserted at the right position
  const itemsToRender = [...items];
  const handleItemClick = (item: { title: string; url: string }) => {
    // If onItemClick returns true, it means the click was handled externally
    if (onItemClick?.(item)) {
      return;
    }

    if (isMobile && item.title !== "Settings") setOpenMobile(false);
    if (item.url.includes("http")) {
      window.open(item.url, "_blank");
    } else {
      startNavigationFeedback(item.url);
      router.push(item.url);
    }
  };

  return (
    <SidebarGroup {...props}>
      <SidebarGroupContent>
        <SidebarMenu className="items-center gap-0">
          {itemsToRender.map((item) => {
            return (
              <React.Fragment key={item.title}>
                {/* Keep Insights above the rocket, then Settings. */}
                {showUpdateDialog && item.title === "Settings" && (
                  <SidebarMenuItem
                    data-rail-key="updates"
                    className="mb-1 last:mb-0"
                  >
                    {updateContent ?? <UpdateDialog />}
                  </SidebarMenuItem>
                )}
                {/* Default */}
                <RailItem
                  visible={item.visible !== false}
                  animated={item.animatePresence === true}
                >
                  <SidebarMenuButton
                    data-rail-key={item.title}
                    variant="custom"
                    aria-label={item.title}
                    disabled={item.visible === false}
                    tooltip={
                      item.visible === false
                        ? undefined
                        : {
                            children: item.title,
                            hidden: false,
                          }
                    }
                    onClick={() => handleItemClick(item)}
                    isActive={
                      (pathname === item.url ||
                        pathname.startsWith(`${item.url}/`)) &&
                      item.title !== "Upgrade to Plus"
                    }
                    className={`flex size-8 !w-8 shrink-0 items-center !justify-center !gap-0 ${item.title === "Settings" && "settings-nav-btn"} ${item.title === "Upgrade to Plus" && "upgrade-nav-btn"}`}
                  >
                    {item.icon && (
                      <item.icon className={`!size-5 ${item.className}`} />
                    )}
                  </SidebarMenuButton>
                  {/* Sibling of the button, not a child — the button's
                    overflow-hidden + rounded corners clip a corner dot */}
                  {item.showDot && (
                    <span
                      aria-hidden="true"
                      data-testid={`nav-dot-${item.title}`}
                      className="pointer-events-none absolute right-0 top-0 z-10 size-1.5 rounded-full bg-red-500"
                    />
                  )}
                </RailItem>
              </React.Fragment>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

function RailItem({
  animated,
  visible,
  children,
}: {
  animated: boolean;
  visible: boolean;
  children: React.ReactNode;
}) {
  return animated ? (
    <AnimatedRailItem visible={visible}>{children}</AnimatedRailItem>
  ) : (
    <SidebarMenuItem className="mb-1 last:mb-0">{children}</SidebarMenuItem>
  );
}
